import React, { useEffect, useState } from 'react';

import Alert from '@mui/material/Alert';
import { Controller, useForm } from 'react-hook-form';
import { Prompt } from 'react-router';
import { useLocation } from 'react-router-dom';

import { DEFAULT_ERROR, useCustomSnackBar } from 'hooks/useCustomSnackBar';
import { useQueryState } from 'hooks/useQueryState';
import useUserHook from 'hooks/useUserHook';

import { QueryParams as TestNarrativeTextQueryParams } from 'api/mutations/testNarrativeText.mutation';
import { QueryParams as UpdateTemplateTextQueryParams } from 'api/mutations/updateTemplateText.mutation';

import SwitchComponent from 'components/common/Switch/Switch';
import { TreeCrumbs } from 'components/common/TreeCrumbs/TreeCrumbs';
import { WidgetWithTitle } from 'components/common/WidgetWithTitle/WidgetWithTitle';

import { NarrativeTextEditor } from 'components/Settings/Components/NarrativeTemplates/components/NarrativeTemplates/NarrativeTextEditor/narrativeTextEditor';
import { useComponentListOptions } from 'components/Settings/Components/NarrativeTemplates/components/NarrativeTemplates/_hooks/useComponentListOptions';
import { useNarrativeTemplatesProvider } from 'components/Settings/Components/NarrativeTemplates/components/NarrativeTemplates/_hooks/useNarrativeTemplatesProvider';
import classes from 'components/Settings/Components/NarrativeTemplates/components/NarrativeTemplates/narrativeTemplates.module.scss';
import { CustomizedButton, CustomizedFormErrorMessage, MyCustomSelect } from 'components/common';

export interface NarrativeTemplateForm {
    component: ISelectOption<string> | undefined;
    narrative: string;
    applyUpdateToAllCustomers: boolean;
}

const TEST_NARRATIVE_ERROR_MESSAGE = 'Error running test template.';

export const NarrativeTemplates = () => {
    //Form config and states
    const {
        formState: { isDirty, errors, isSubmitted },
        control,
        handleSubmit,
        getValues,
        watch,
        setValue,
        resetField,
    } = useForm<NarrativeTemplateForm>({
        defaultValues: {
            applyUpdateToAllCustomers: false,
        },
    });
    const narrativeValueFromForm = watch('narrative');
    const componentFromForm = watch('component');

    //API
    const { queryData, queryMutations, queryUtils } = useNarrativeTemplatesProvider(componentFromForm);
    const { contract, projectHierarchyList, latestVersion, baselineVersion } = queryData.project;

    //Hooks
    const [componentKeyUrlParam, setComponentKeyUrlParam] = useQueryState('componentKey');
    const { handleSnackBar } = useCustomSnackBar();
    const { ability } = useUserHook();
    const isAdmin = ability.can('all', 'Admin');
    const currentLocation = useLocation();
    const componentListOptions = useComponentListOptions(queryData.componentList);

    const [testValue, setTestValue] = useState<string>(queryMutations.testNarrativeText.data ?? ' ');

    const syncNarrativeValueToApiResponseOrEmpty = () => {
        let narrativeValue = '';
        if (queryData.narrativeTemplatePerComponent?.text) {
            narrativeValue = queryData.narrativeTemplatePerComponent.text;
        }
        setValue('narrative', narrativeValue);
    };

    useEffect(() => {
        syncNarrativeValueToApiResponseOrEmpty();
    }, [queryData.narrativeTemplatePerComponent?.text, componentFromForm]);

    useEffect(() => {
        //Select component from components dropdown if componentKey url param provided
        if (!componentFromForm && componentKeyUrlParam && componentListOptions && componentListOptions.length > 0) {
            const initialValue = componentListOptions.find((item) => item.value === componentKeyUrlParam);
            if (initialValue) {
                setValue('component', initialValue);
            }
        }
    }, [componentListOptions, componentFromForm]);

    const onRunTestTemplate = () => {
        const value = getValues('narrative');

        if (baselineVersion && baselineVersion.id && latestVersion && latestVersion.id && componentFromForm) {
            const payload: TestNarrativeTextQueryParams = {
                compareMetaDataId: baselineVersion.id,
                actualMetaDataId: latestVersion.id,
                narrativeText: value,
                filter: {
                    filters: undefined,
                },
                componentKey: componentFromForm?.value,
            };
            queryMutations.testNarrativeText.mutate(payload, {
                onSuccess: (value) => {
                    setTestValue(value);
                },
                onError: (error) => {
                    setTestValue(TEST_NARRATIVE_ERROR_MESSAGE);
                    DEFAULT_ERROR({ error, handleError: handleSnackBar });
                },
            });
        }
    };

    const onSubmit = (data: NarrativeTemplateForm) => {
        const { component, narrative, applyUpdateToAllCustomers } = data;

        if (!component?.value || component.id == null || isNaN(Number(component.id))) {
            return;
        }

        const payload: UpdateTemplateTextQueryParams = {
            componentKey: component.value,
            componentId: Number(component.id),
            text: narrative,
            forCurrentCustomerOnly: !applyUpdateToAllCustomers,
        };

        queryMutations.updateTemplateText.mutate(payload, {
            onSuccess: () => {
                handleSnackBar('Successfully saved!', 'success');
                resetField('applyUpdateToAllCustomers');
            },
            onError: (error) => {
                DEFAULT_ERROR({ error, handleError: handleSnackBar });
            },
            onSettled: () => {
                queryUtils.queryClient.invalidateQueries('getTemplateByKey');
            },
        });
    };

    return (
        <WidgetWithTitle
            id={'narrativeTemplatesForm_FormTitle'}
            title={'Edit Templates'}
            className={classes.narrativeTemplatesContainer}
        >
            <div>
                <form className={classes.userProfileForm} onSubmit={handleSubmit(onSubmit)}>
                    <div className={classes.componentSelectSection}>
                        <label>Select Component:</label>
                        <Controller
                            render={({ field }) => (
                                <MyCustomSelect<ISelectOption<string>>
                                    {...field}
                                    onChange={(value) => {
                                        setTestValue(' ');
                                        field.onChange(value);
                                        setComponentKeyUrlParam(value?.value);
                                    }}
                                    options={componentListOptions}
                                    isError={!!errors.component}
                                />
                            )}
                            name="component"
                            control={control}
                            rules={{ required: 'Please select component' }}
                        />
                        {errors.component && <CustomizedFormErrorMessage text={errors.component.message} />}
                    </div>

                    <div className={classes.componentNarrativeTemplateSection}>
                        <div className={classes.labelContainer}>
                            <label>Component Narrative Template:</label>
                            {errors.narrative && <CustomizedFormErrorMessage text={errors.narrative.message} />}
                        </div>
                        <Alert severity="info" className={classes.helpTextAlertInfo}>
                            Type [ (square bracket) to open list of available component parameters.
                        </Alert>
                        <Controller
                            render={({ field }) => (
                                <NarrativeTextEditor
                                    placeholder={
                                        'Enter template here. Type [ (square bracket) to open list of available component parameters.'
                                    }
                                    value={field.value}
                                    onChange={field.onChange}
                                    isError={!!errors.narrative}
                                    disabled={componentFromForm === null || componentFromForm === undefined}
                                    templatePlaceholders={queryData.narrativeTemplatePerComponent?.placeholders}
                                />
                            )}
                            name="narrative"
                            control={control}
                            rules={{ required: 'Narrative template must not be empty' }}
                        />
                    </div>

                    <div className={classes.testProject}>
                        <div className={classes.testProjectTitle}>
                            <CustomizedButton
                                size={'medium'}
                                color={'secondary'}
                                disabled={!narrativeValueFromForm?.length || queryMutations.testNarrativeText.isLoading}
                                isLoading={queryMutations.testNarrativeText.isLoading}
                                onClick={onRunTestTemplate}
                            >
                                Test Template
                            </CustomizedButton>
                            <div className={classes.projectAndContract}>
                                <label>Test Project:</label>
                                {contract && projectHierarchyList && (
                                    <TreeCrumbs
                                        selectedNode={contract.uuid}
                                        data={projectHierarchyList}
                                        isDisabled={true}
                                    />
                                )}
                            </div>
                            {latestVersion && (
                                <div className={classes.versionWrapper}>
                                    <label>Version:</label>
                                    <span className={classes.versionValue}>{latestVersion.versionName} | Latest</span>
                                </div>
                            )}
                            {baselineVersion && (
                                <div className={classes.versionWrapper}>
                                    <label>Compare to:</label>
                                    <span className={classes.versionValue}>
                                        {baselineVersion.versionName} | Baseline
                                    </span>
                                </div>
                            )}
                        </div>
                        <div
                            className={classes.testPreview}
                            dangerouslySetInnerHTML={{
                                __html: testValue,
                            }}
                        />
                    </div>

                    <div className={classes.bottomSection}>
                        {isAdmin && (
                            <div>
                                <label>Apply update to all customers: </label>
                                <Controller
                                    render={({ field }) => (
                                        <SwitchComponent onChange={field.onChange} checked={field.value} />
                                    )}
                                    name="applyUpdateToAllCustomers"
                                    control={control}
                                />
                            </div>
                        )}

                        <div className={classes.buttonsWrapperRight}>
                            <CustomizedButton
                                size={'large'}
                                color={'primary'}
                                type="submit"
                                isLoading={queryMutations.updateTemplateText.isLoading}
                            >
                                Save
                            </CustomizedButton>

                            <CustomizedButton
                                size={'large'}
                                color={'secondary'}
                                onClick={() => syncNarrativeValueToApiResponseOrEmpty()}
                            >
                                Revert Changes
                            </CustomizedButton>
                        </div>
                    </div>
                </form>
            </div>

            <Prompt
                when={isDirty && !isSubmitted}
                message={(location) => {
                    const isOnlyQueryChanged = location.pathname === currentLocation.pathname;

                    if (isOnlyQueryChanged) return true; // allow silently
                    return 'You have unsaved changes. Are you sure you want to leave?';
                }}
            />
        </WidgetWithTitle>
    );
};
